import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isString from 'lodash/isString'
import toNumber from 'lodash/toNumber'

// Authoritative Gatekeeper pricing (PHP ₱, Pricing v3). All amounts are whole
// pesos, so option totals are always integers.
export const EQUIPMENT_ONE_TIME = 42240
export const PER_ID_OPTION_1 = 180
export const PER_ID_OPTION_2 = 230
export const OPTION_2_MIN_STUDENTS = 300

export enum PricingOptionEnum {
  Option1 = 'option1',
  Option2 = 'option2',
}

export type GatekeeperPricing = {
  option1Total: number
  // null when Option 2 is unavailable (student count below the minimum).
  option2Total: number | null
  option2Available: boolean
  recommended: PricingOptionEnum
}

// Option 1 = one-time equipment + per-ID; Option 2 = all-inclusive per-ID, only
// when enrollment meets the minimum. The recommended option is the lower of the
// two AVAILABLE totals — the break-even point falls out of that comparison, so
// it is never hardcoded.
export const computeGatekeeperPricing = (studentCount: number): GatekeeperPricing => {
  const option1Total = EQUIPMENT_ONE_TIME + PER_ID_OPTION_1 * studentCount

  const option2Available = studentCount >= OPTION_2_MIN_STUDENTS
  const option2Total = option2Available ? PER_ID_OPTION_2 * studentCount : null

  const option2IsCheaper = !isNil(option2Total) && option2Total < option1Total
  const recommended = option2IsCheaper ? PricingOptionEnum.Option2 : PricingOptionEnum.Option1

  return { option1Total, option2Total, option2Available, recommended }
}

// Coerce raw user input into a usable student count. Blank / non-numeric /
// negative values yield null so callers render no result instead of crashing;
// fractional values floor to a whole student.
export const normalizeStudentCount = (raw: unknown): number | null => {
  if (isNil(raw)) return null
  if (isString(raw) && isEmpty(raw.trim())) return null

  const parsed = toNumber(raw)
  if (Number.isNaN(parsed) || parsed < 0) return null

  return Math.floor(parsed)
}
